// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadFeaturedCategories from "@turnerBlueprint/App/Handlers/XNG/FeaturedCategories";
import { CustomAppState } from "@turnerBlueprint/App/Store/Reducers";
import GridContainer from "@turnerBlueprint/MobiusOverrides/GridContainer/GridContainer";
import { XNGAGE_CUSTOM_WIDGET } from "@turnerBlueprint/XDL/constants";
import React, { FC, useEffect } from "react";
import { ResolveThunks, connect } from "react-redux";

const enum fields {
    parentCategory = "parentCategory",
    categoryToDisplayList = "categoryToDisplayList",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.parentCategory]: string[];
        [fields.categoryToDisplayList]: string[];
    };
}

export interface FeaturedCategoriesStyles {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyProps;
    cardContainer?: GridItemProps;
    cardContainerLink?: LinkPresentationProps;
    link?: LinkPresentationProps;
    centeringWrapper?: InjectableCss;
    box?: GridContainerProps;
    subtitle?: TypographyPresentationProps;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const subcategories = props.fields?.categoryToDisplayList || [];
    const categoryIds = [props.fields.parentCategory?.[0], ...subcategories].filter(x => x);
    const viewKey = getDataViewKey({ categories: categoryIds });
    const categoriesState = (state as CustomAppState).xFeaturedCategories.views?.[viewKey];
    return {
        parentCategory: categoriesState?.categories?.[0],
        subCategories: categoriesState?.categories?.slice(1),
        isLoading: categoriesState?.loading,
    };
};

const mapDispatchToProps = {
    loadFeaturedCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const featuredCategoriesStyles: FeaturedCategoriesStyles = {};

const styles = featuredCategoriesStyles;
const FeaturedCategories: FC<Props> = ({
    fields,
    parentCategory,
    subCategories,
    isLoading,
    loadFeaturedCategories,
}) => {
    useEffect(() => {
        const subcategories = fields?.categoryToDisplayList || [];
        const categoryIds = [fields.parentCategory?.[0], ...subcategories].filter(x => x);
        if (!isLoading && !parentCategory && fields.parentCategory?.[0]) {
            loadFeaturedCategories({
                categories: categoryIds,
            });
        }
    }, [isLoading, fields.parentCategory, subCategories]);

    let titleWrapper: React.ReactNode | null = null;
    if (parentCategory?.shortDescription) {
        titleWrapper = (
            <StyledWrapper {...styles.titleWrapper}>
                {parentCategory?.shortDescription && (
                    <Typography {...styles.title}>{parentCategory.shortDescription}</Typography>
                )}
                {parentCategory?.path && (
                    <Link href={parentCategory.path} area-label={parentCategory.shortDescription} {...styles.link}>
                        {translate("View Category")}
                    </Link>
                )}
            </StyledWrapper>
        );
    }

    if (isLoading) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="featured-categories-widget">
            {titleWrapper}
            <GridContainer {...styles.box}>
                {subCategories?.map((item, index) => {
                    return (
                        <GridItem {...styles.cardContainer} key={item.id}>
                            <Link {...styles.cardContainerLink} href={item.path}>
                                <LazyImage
                                    src={item.smallImagePath}
                                    altText={item.shortDescription}
                                    width="98px"
                                    height="98px"
                                    imgProps={{ "aria-label": `Go Category ${item.shortDescription}` }}
                                />
                                <Typography {...styles.subtitle}>{item.shortDescription}</Typography>
                            </Link>
                        </GridItem>
                    );
                })}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FeaturedCategories),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.parentCategory,
                editorTemplate: "CategoriesField",
                singleCategorySelection: item => true,
                displayName: "Heading Category",
                defaultValue: [],
                fieldType: "General",
            },

            {
                name: fields.categoryToDisplayList,
                editorTemplate: "CategoriesField",
                defaultValue: [],
                displayName: "Categories to Display",
                fieldType: "General",
                validate: item => (item.length > 4 ? "You can select up to 4 categories!" : ""),
            },
        ],
    },
};

export default widgetModule;
